import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import ServiceCategoryEditorDialog from './ServiceCategoryEditorDialog';
import SortableServiceCategoryList from './SortableServiceCategoryList';
import { useToast } from '../../context/ToastContext';
import ServiceCategoryService from '../../services/serviceCategoryService';
import { getLocalizedValue } from '../../constants/Languages';
import requestDeduplicator from '../../utils/requestDeduplicator';

const CLOSED_DIALOG_STATE = { open: false, mode: 'create', category: null };

const ServiceCategoriesPanel = ({ storeId, canCreate, canEdit, canDelete }) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState(['en']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG_STATE);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const loadData = useCallback(async () => {
    if (!storeId) {
      setError(t('serviceCategoriesManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    const requestKey = `service-categories-management-${storeId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await ServiceCategoryService.getManagementData(storeId);
        setCategories(data.categories);
        setServices(data.services);
        setSupportedLanguages(data.supportedLanguages);
      } catch (loadError) {
        console.error('Failed to load service categories:', loadError);
        setError(t('serviceCategoriesManagement.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const persistReorder = async (nextCategories) => {
    const previousCategories = categories;
    setCategories(nextCategories);

    try {
      setReordering(true);
      await ServiceCategoryService.reorderCategories(
        storeId,
        nextCategories.map((category) => category.id)
      );
    } catch (reorderError) {
      console.error('Failed to reorder categories:', reorderError);
      setCategories(previousCategories);
      showError(t('serviceCategoriesManagement.error.reorder'));
    } finally {
      setReordering(false);
    }
  };

  const handleSaveCategory = async (category) => {
    try {
      setSaving(true);

      if (dialogState.mode === 'edit' && dialogState.category?.id) {
        await ServiceCategoryService.updateCategory(storeId, dialogState.category.id, category);
        showSuccess(t('serviceCategoriesManagement.feedback.updated'));
      } else {
        await ServiceCategoryService.createCategory(storeId, category);
        showSuccess(t('serviceCategoriesManagement.feedback.created'));
      }

      setDialogState(CLOSED_DIALOG_STATE);
      await loadData();
    } catch (saveError) {
      console.error('Failed to save category:', saveError);
      showError(t(dialogState.mode === 'edit'
        ? 'serviceCategoriesManagement.error.update'
        : 'serviceCategoriesManagement.error.create'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    const target = categoryToDelete;
    if (!target) {
      return;
    }

    try {
      setSaving(true);
      await ServiceCategoryService.deleteCategory(storeId, target.id);
      showSuccess(t('serviceCategoriesManagement.feedback.deleted'));
      setCategoryToDelete(null);
      await loadData();
    } catch (deleteError) {
      console.error('Failed to delete category:', deleteError);
      showError(t('serviceCategoriesManagement.error.delete'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {canCreate ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mb: { xs: 2.5, sm: 4 },
            pt: { xs: 0.5, sm: 1 },
          }}
        >
          <PrimaryActionButton
            startIcon={<AddIcon />}
            onClick={() => setDialogState({ open: true, mode: 'create', category: null })}
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 1.5,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('serviceCategoriesManagement.addNew')}
          </PrimaryActionButton>
        </Box>
      ) : null}

      {categories.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('serviceCategoriesManagement.noCategories')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('serviceCategoriesManagement.emptyHint')}
          </Typography>
        </Box>
      ) : (
        <SortableServiceCategoryList
          categories={categories}
          loading={saving || reordering}
          canEdit={canEdit}
          canDelete={canDelete}
          onReorder={persistReorder}
          onEdit={(category) => setDialogState({ open: true, mode: 'edit', category })}
          onDelete={setCategoryToDelete}
        />
      )}

      <ServiceCategoryEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        category={dialogState.category}
        supportedLanguages={supportedLanguages}
        services={services}
        saving={saving}
        onClose={() => setDialogState(CLOSED_DIALOG_STATE)}
        onSave={handleSaveCategory}
      />

      <ConfirmDialog
        open={Boolean(categoryToDelete)}
        onClose={() => setCategoryToDelete(null)}
        onConfirm={handleDeleteCategory}
        title={t('serviceCategoriesManagement.delete.title')}
        message={t('serviceCategoriesManagement.delete.message', {
          name: categoryToDelete
            ? getLocalizedValue(categoryToDelete.name, i18n.language, undefined, '')
            : '',
        })}
        confirmText={t('serviceCategoriesManagement.delete.confirm')}
        cancelText={t('common.cancel')}
      />
    </Box>
  );
};

export default ServiceCategoriesPanel;
